import React, { useEffect, useState, useRef, useMemo } from 'react';
import { ScannerSection } from '../scanner';
import {
  isFadingQrScanError,
  QR_MISS_FADE_MS,
  QR_NOT_FOUND_MESSAGE,
} from '../scanner/qrScanMessages';
import { getStorageIdPrefixes, getBrowseIndexes } from '../../services/shelfIndexAPI';
import type { BrowseIndexesEntry } from '../../types/apiTypes';
import { formatType } from '../../utils/shelfIndexUtils';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useReprintByPrefix } from '../../hooks/useReprintByPrefix';
import { printRunProgressMessage } from '../../hooks/usePrintOrderRun';
import { parseStorageIdPrefixParam } from '../../utils/scanLabelHandoff';
import type { PrintOrderSource } from '@/shared/types/printOrder.types';
import type { DestinationPrinterId } from '@/shared/types/printer.types';
import './ReprintLabelPanel.css';

const REPRINT_MAX_COPIES = 8;

export function extractPrefixFromQr(qrData: string | null | undefined): string | null {
  if (!qrData || typeof qrData !== 'string') return null;
  const cleaned = qrData.replace(/\s+/g, '').replace(/-/g, '').trim();
  const hexPart = cleaned.match(/^[0-9a-fA-F]+/);
  if (hexPart && hexPart[0].length >= 6) {
    return hexPart[0].substring(0, 6).toLowerCase();
  }
  return null;
}

interface ReprintLabelPanelProps {
  readonly source: PrintOrderSource;
  readonly printerId: DestinationPrinterId;
  readonly initialStorageIdPrefix?: string;
}

/**
 * Scan a label's QR code (or pick a storage ID) and reprint it.
 * Reprints run as tracked print orders — no shelf_indexes row is written.
 */
function ReprintLabelPanel({ source, printerId, initialStorageIdPrefix }: ReprintLabelPanelProps) {
  const [storageIdInput, setStorageIdInput] = useState('');
  const [reprintMessage, setReprintMessage] = useState('');
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualStorageId, setManualStorageId] = useState('');
  const [storageIdPrefixes, setStorageIdPrefixes] = useState<string[]>([]);
  const [filteredPrefixes, setFilteredPrefixes] = useState<string[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [prefixesLoading, setPrefixesLoading] = useState(true);
  const [browseExpanded, setBrowseExpanded] = useState(false);
  const [browseEntries, setBrowseEntries] = useState<BrowseIndexesEntry[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseHasMore, setBrowseHasMore] = useState(false);
  const [browseOffset, setBrowseOffset] = useState(0);
  const [selectedBrowseId, setSelectedBrowseId] = useState<string | null>(null);
  const [printLabelCount, setPrintLabelCount] = useState(1);
  const [printBigLabelCount, setPrintBigLabelCount] = useState(1);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const skipAutocompleteOpenRef = useRef<boolean>(false);
  const initialPrefixAppliedRef = useRef(false);
  const [qrMissKey, setQrMissKey] = useState(0);

  const { reprint, isRunning: reprintLoading } = useReprintByPrefix(source);

  const prefixSet = useMemo(() => {
    return new Set(storageIdPrefixes.map((p) => p.toLowerCase()));
  }, [storageIdPrefixes]);

  useEffect(() => {
    const fetchPrefixes = async () => {
      setPrefixesLoading(true);
      try {
        const prefixes = await getStorageIdPrefixes();
        setStorageIdPrefixes(prefixes);
      } catch (error: unknown) {
        console.error('Error loading storage ID prefixes:', error);
        setIsErrorMessage(true);
        setReprintMessage('Error loading storage IDs. Please refresh.');
      } finally {
        setPrefixesLoading(false);
      }
    };
    fetchPrefixes();
  }, []);

  useEffect(() => {
    if (initialPrefixAppliedRef.current) return;
    const prefix = parseStorageIdPrefixParam(initialStorageIdPrefix);
    if (!prefix) return;
    initialPrefixAppliedRef.current = true;
    setStorageIdInput(prefix);
  }, [initialStorageIdPrefix]);

  useEffect(() => {
    if (skipAutocompleteOpenRef.current) {
      skipAutocompleteOpenRef.current = false;
      if (manualStorageId.length > 0) {
        const searchTerm = manualStorageId.toLowerCase().trim();
        const filtered = storageIdPrefixes
          .filter((p) => p.toLowerCase().includes(searchTerm))
          .slice(0, 10);
        setFilteredPrefixes(filtered);
      }
      setSelectedIndex(-1);
      setShowAutocomplete(false);
      return;
    }
    if (manualStorageId.length > 0) {
      const searchTerm = manualStorageId.toLowerCase().trim();
      const filtered = storageIdPrefixes
        .filter((p) => p.toLowerCase().includes(searchTerm))
        .slice(0, 10);
      setFilteredPrefixes(filtered);
      setShowAutocomplete(filtered.length > 0);
    } else {
      setFilteredPrefixes([]);
      setShowAutocomplete(false);
    }
    setSelectedIndex(-1);
  }, [manualStorageId, storageIdPrefixes]);

  useEffect(() => {
    if (showAutocomplete && inputRef.current && autocompleteRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const dropdown = autocompleteRef.current;
      dropdown.style.top = `${inputRect.bottom}px`;
      dropdown.style.left = `${inputRect.left}px`;
      dropdown.style.width = `${inputRect.width}px`;
    }
  }, [showAutocomplete, manualStorageId, filteredPrefixes]);

  useClickOutside(
    [autocompleteRef, inputRef],
    () => setShowAutocomplete(false),
  );

  useEffect(() => {
    if (!browseExpanded) return;
    const fetchBrowse = async () => {
      setBrowseLoading(true);
      try {
        const { entries, hasMore } = await getBrowseIndexes(0, 20);
        setBrowseEntries(entries);
        setBrowseHasMore(hasMore);
        setBrowseOffset(entries.length);
      } catch (err: unknown) {
        console.error('Error loading browse indexes:', err);
        setBrowseEntries([]);
        setBrowseHasMore(false);
      } finally {
        setBrowseLoading(false);
      }
    };
    fetchBrowse();
  }, [browseExpanded]);

  const handleShowMoreBrowse = async () => {
    setBrowseLoading(true);
    try {
      const { entries, hasMore } = await getBrowseIndexes(browseOffset, 20);
      setBrowseEntries((prev) => [...prev, ...entries]);
      setBrowseHasMore(hasMore);
      setBrowseOffset((prev) => prev + entries.length);
    } catch (err: unknown) {
      console.error('Error loading more browse indexes:', err);
      setBrowseHasMore(false);
    } finally {
      setBrowseLoading(false);
    }
  };

  const showMessage = (text: string, isError: boolean, clearAfterMs = 3000): void => {
    setIsErrorMessage(isError);
    setReprintMessage(text);
    setTimeout(() => {
      setReprintMessage('');
      setIsErrorMessage(false);
    }, clearAfterMs);
  };

  const handleReprintCapture = (qrData: string | null): boolean => {
    if (!qrData) return false;
    const prefix = extractPrefixFromQr(qrData);
    if (prefix) {
      setStorageIdInput(prefix);
      if (prefixSet.has(prefix)) {
        showMessage(`Matched storage ID prefix ${prefix}.`, false);
        return true;
      }
      showMessage('ID not found in database.', true);
      return false;
    }
    setQrMissKey((key) => key + 1);
    showMessage(QR_NOT_FOUND_MESSAGE, true, QR_MISS_FADE_MS);
    return false;
  };

  const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9a-fA-F]/g, '').substring(0, 6);
    setManualStorageId(value.toLowerCase());
  };

  const handleManualInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete || filteredPrefixes.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredPrefixes.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      selectPrefix(filteredPrefixes[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const selectPrefix = (prefix: string) => {
    skipAutocompleteOpenRef.current = true;
    setManualStorageId(prefix);
    setStorageIdInput(prefix);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    setShowManualInput(false);
    showMessage(`Storage ID ${prefix} set.`, false, 2000);
  };

  const handleManualInputSubmit = () => {
    const value = manualStorageId.trim().toLowerCase();
    if (value.length === 6) {
      setStorageIdInput(value);
      setShowManualInput(false);
      setManualStorageId('');
      showMessage(`Storage ID ${value} set.`, false, 2000);
    } else {
      showMessage('Please enter all 6 characters.', true, 2000);
    }
  };

  const handleManualInputClose = () => {
    setShowManualInput(false);
    setManualStorageId('');
  };

  const hasValidStorageId =
    storageIdInput.trim().length === 6 && prefixSet.has(storageIdInput.trim().toLowerCase());
  const prefix = storageIdInput.trim().toLowerCase();

  /** Reprint the loaded label as a tracked print order. */
  const runReprint = async (isBigLabel: boolean, copies: number): Promise<void> => {
    if (!hasValidStorageId || reprintLoading) return;

    setIsErrorMessage(false);
    setReprintMessage(copies > 1 ? `Printing ${copies} labels...` : 'Printing...');

    const result = await reprint({
      storageIdPrefix: prefix,
      isBigLabel,
      copies,
      printerId,
      onProgress: (progress) => {
        setReprintMessage(printRunProgressMessage(progress));
      },
    });

    showMessage(result.message, !result.completed, result.completed ? 3000 : 10000);
  };

  return (
    <>
      <p className="rwl-desc">Scan QR code or enter storage ID to load a label, then reprint.</p>

      {prefixesLoading && (
        <div className="pbl-loading">
          <div className="pbl-loading-spinner" />
          <div className="pbl-loading-text">Loading storage IDs...</div>
        </div>
      )}

      {reprintMessage && !isFadingQrScanError(reprintMessage) && (
        <div className={`pbl-message${isErrorMessage ? ' pbl-message-error' : ''}`}>{reprintMessage}</div>
      )}

      {!prefixesLoading && (
        <>
          <div className="pbl-section">
            <div className="pbl-section-title">
              <span className="pbl-section-text">Scan QR code</span>
            </div>
            <ScannerSection
              mode="qr"
              onCapture={handleReprintCapture}
              className="webcam-capture-container"
              overlay={
                isFadingQrScanError(reprintMessage) ? (
                  <div key={qrMissKey} className="webcam-qr-miss-error" role="status">
                    {reprintMessage}
                  </div>
                ) : null
              }
            />
          </div>

          <div className="pbl-section">
            <div className="pbl-section-title pbl-section-info">
              <span className="pbl-section-text">Storage ID</span>
            </div>
            <div className="pbl-info-display">
              <div className="pbl-info-row">
                <span className="pbl-info-label">Storage ID:</span>
                <div className="pbl-info-value-wrapper">
                  <span
                    className={`pbl-info-value pbl-info-clickable ${!storageIdInput ? 'pbl-info-empty' : ''}`}
                    onClick={() => {
                      setShowManualInput(true);
                      setManualStorageId(storageIdInput || '');
                      setTimeout(() => {
                        const modal = document.querySelector('.pbl-modal-overlay');
                        if (modal) modal.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        if (inputRef.current) {
                          inputRef.current.focus();
                          const len = inputRef.current.value.length;
                          inputRef.current.setSelectionRange(len, len);
                        }
                      }, 100);
                    }}
                  >
                    {storageIdInput || 'Scan QR or enter storage ID'}
                  </span>
                  {storageIdInput && (
                    <button
                      className="pbl-info-clear"
                      onClick={(e) => {
                        e.stopPropagation();
                        setStorageIdInput('');
                        setSelectedBrowseId(null);
                        showMessage('Storage ID cleared', false, 2000);
                      }}
                      title="Clear Storage ID"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {hasValidStorageId && (
            <div className="pbl-submit-section">
              <div className="pbl-section-title pbl-section-info">
                <span className="pbl-section-text">Reprint Label</span>
              </div>
              <div className="pbl-print-buttons">
                <div className="pbl-count-row">
                  <button
                    type="button"
                    className="pbl-increment-btn"
                    onClick={() => setPrintLabelCount((c) => Math.max(1, c - 1))}
                    disabled={reprintLoading || printLabelCount <= 1}
                    aria-label="Decrease label count"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    onClick={() => { void runReprint(false, printLabelCount); }}
                    disabled={reprintLoading}
                    className="pbl-print-button pbl-print-button-in-row"
                  >
                    {reprintLoading ? 'Printing...' : `Print ${printLabelCount} Label${printLabelCount !== 1 ? 's' : ''}`}
                  </button>
                  <button
                    type="button"
                    className="pbl-increment-btn"
                    onClick={() => setPrintLabelCount((c) => Math.min(REPRINT_MAX_COPIES, c + 1))}
                    disabled={reprintLoading || printLabelCount >= REPRINT_MAX_COPIES}
                    aria-label="Increase label count"
                  >
                    +
                  </button>
                </div>
                <div className="pbl-count-row">
                  <button
                    type="button"
                    className="pbl-increment-btn"
                    onClick={() => setPrintBigLabelCount((c) => Math.max(1, c - 1))}
                    disabled={reprintLoading || printBigLabelCount <= 1}
                    aria-label="Decrease big label count"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    onClick={() => { void runReprint(true, printBigLabelCount); }}
                    disabled={reprintLoading}
                    className="pbl-print-big-button pbl-print-button-in-row"
                  >
                    {reprintLoading ? 'Printing...' : `Print ${printBigLabelCount} Big Label${printBigLabelCount !== 1 ? 's' : ''}`}
                  </button>
                  <button
                    type="button"
                    className="pbl-increment-btn"
                    onClick={() => setPrintBigLabelCount((c) => Math.min(REPRINT_MAX_COPIES, c + 1))}
                    disabled={reprintLoading || printBigLabelCount >= REPRINT_MAX_COPIES}
                    aria-label="Increase big label count"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="rwl-browse-section">
            <div
              className="rwl-browse-title"
              onClick={() => setBrowseExpanded((prev) => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setBrowseExpanded((prev) => !prev);
                }
              }}
            >
              <span className="pbl-section-text">Browse Indexes</span>
              <span className={`rwl-browse-triangle ${browseExpanded ? 'expanded' : ''}`}>▶</span>
            </div>
            {browseExpanded && (
              <div className="rwl-browse-content">
                {browseLoading && browseEntries.length === 0 ? (
                  <div className="rwl-browse-empty">Loading...</div>
                ) : browseEntries.length === 0 ? (
                  <div className="rwl-browse-empty">No entries found</div>
                ) : (
                  <>
                    <table className="rwl-browse-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>SKU</th>
                          <th>Shelf Index</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {browseEntries.map((entry, idx) => {
                          const selectEntry = (): void => {
                            setSelectedBrowseId(entry.id);
                            setStorageIdInput(entry.id);
                            showMessage(`Storage ID ${entry.id} set.`, false, 2000);
                          };
                          return (
                            <tr
                              key={`${entry.id}-${idx}`}
                              className={selectedBrowseId === entry.id ? 'rwl-browse-row-selected' : ''}
                              onClick={selectEntry}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  selectEntry();
                                }
                              }}
                            >
                              <td>{entry.id}</td>
                              <td className="rwl-browse-sku">{entry.sku}</td>
                              <td className="rwl-browse-shelf">{entry.shelf_index}</td>
                              <td>{formatType(entry.type)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {browseHasMore && (
                      <button
                        type="button"
                        className="rwl-browse-more"
                        onClick={handleShowMoreBrowse}
                        disabled={browseLoading}
                      >
                        {browseLoading ? 'Loading...' : 'Show more entries'}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {showManualInput && (
            <div className="pbl-modal-overlay" onClick={handleManualInputClose}>
              <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="pbl-modal-close" onClick={handleManualInputClose}>
                  ×
                </button>
                <div className="pbl-modal-title">Enter Storage ID (first 6 chars)</div>
                <div className="pbl-manual-popup">
                  <div className="pbl-sku-input-wrapper">
                    <input
                      ref={inputRef}
                      type="text"
                      value={manualStorageId}
                      maxLength={6}
                      onChange={handleManualInputChange}
                      onKeyDown={handleManualInputKeyDown}
                      placeholder="e.g. a1b2c3"
                      className="pbl-sku-input"
                      onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter' && selectedIndex < 0) handleManualInputSubmit();
                      }}
                    />
                    {showAutocomplete && filteredPrefixes.length > 0 && (
                      <div ref={autocompleteRef} className="pbl-autocomplete-dropdown">
                        {filteredPrefixes.map((entryPrefix, index) => (
                          <div
                            key={entryPrefix}
                            className={`pbl-autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
                            onClick={() => selectPrefix(entryPrefix)}
                            onMouseEnter={() => setSelectedIndex(index)}
                          >
                            {entryPrefix}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleManualInputSubmit}
                    className="pbl-submit-button"
                    disabled={manualStorageId.trim().length !== 6}
                  >
                    Set Storage ID
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default ReprintLabelPanel;
